/**
 * Paste guard — decides whether a plain-text clipboard paste should be
 * inlined into the textarea (browser default) or converted into a
 * collapsed attachment chip held above the composer.
 *
 * Why this exists: inlining a >4KB HTML block into the user turn blows
 * the prompt-assembler context budget. Diagnostic fixtures traced a
 * silent loop in context assembly that emitted bare "Opened it." /
 * "Done." in place of real work (bug-stuck-on-paste).
 *
 * The threshold is configurable via the `thresholdBytes` argument so the
 * composer can centralize the constant and tests can pin a tight
 * boundary without caring about the default.
 */
export const DEFAULT_PASTE_ATTACHMENT_THRESHOLD_BYTES = 4096;

export interface PasteGuardDecision {
  /** `true` when the paste should be converted into a chip (caller must
   *  call `preventDefault()` and push a `PastedAttachment` into its
   *  composer state). `false` when the paste falls through to the
   *  browser's default insert behavior. */
  attach: boolean;
  /** Byte size of the pasted text (always set so callers can log it). */
  byteSize: number;
  /** Human-readable chip label (`"Pasted content — 12KB"`). Only set
   *  when `attach` is `true`. */
  label?: string;
}

/**
 * Decide whether the pasted text should become a chip. Pure function —
 * no DOM access, safe to unit-test. Uses `Blob` to compute the UTF-8
 * byte size so emoji / CJK text is counted honestly (1 char ≠ 1 byte).
 *
 * Empty pastes fall through (browser default — effectively a no-op).
 */
export function decidePasteGuard(
  text: string,
  thresholdBytes: number = DEFAULT_PASTE_ATTACHMENT_THRESHOLD_BYTES,
): PasteGuardDecision {
  if (!text) {
    return { attach: false, byteSize: 0 };
  }
  const byteSize = new Blob([text]).size;
  if (byteSize <= thresholdBytes) {
    return { attach: false, byteSize };
  }
  const kb = Math.max(1, Math.round(byteSize / 1024));
  return {
    attach: true,
    byteSize,
    label: `Pasted content — ${kb}KB`,
  };
}
